import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { isNullOrUndefined } from 'Util/TypeGuards';

/**
 * Returns true if the transaction is associated with bookings that only have one alteration with RESERVED status.
 */
export async function transactionHasOnlyNewBookings(transactionId: string | null): Promise<boolean> {
	if (transactionId === null) {
		return false;
	}

	try {
		const response = await axios.get<boolean>(
			`/api/entity/TransactionEntity/${transactionId}/has-only-new-bookings`,
		);

		const hasOnlyNewBookings = response.data;

		if (isNullOrUndefined(hasOnlyNewBookings)) {
			console.error('Something went wrong while checking if transaction only has new bookings.');
			return false;
		}

		return hasOnlyNewBookings;
	} catch (e) {
		console.error('Something went wrong while checking if transaction only has new bookings.', e);
		return false;
	}
}

/** Long enough to cover a remount of the payment form; shorter than a trip back to the cart and out again. */
const HAS_ONLY_NEW_BOOKINGS_STALE_MS = 5_000;

/**
 * Hook for transactionHasOnlyNewBookings. Undefined until the answer is known.
 *
 * A query, so a remount of the payment form reuses the answer for the same transaction. Keyed on
 * transactionId: the payment flow reuses the same mounted component across transaction/customer
 * switches, so the answer must follow the transaction rather than freeze at the first one's value,
 * leaving canInvoice / the "previously paid" disabled note reading stale state.
 *
 * A cached answer is only used while fresh, never shown during its revalidation: the transaction's
 * bookings can change in the cart between visits to the payment step, and a stale `true` would offer
 * — and pre-select — Send invoice for a transaction that can no longer be invoiced.
 */
export function useTransactionHasOnlyNewBookings(transactionId: string | null): boolean | undefined {
	const query = useQuery({
		queryKey: ['transaction-has-only-new-bookings', transactionId],
		queryFn: () => transactionHasOnlyNewBookings(transactionId),
		staleTime: HAS_ONLY_NEW_BOOKINGS_STALE_MS,
	});
	return query.isFetching ? undefined : query.data;
}
